import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of, tap } from 'rxjs';
import { ApiClientService } from './api-client.service';

export interface AgGridLicenseResponse {
  license: string;
}

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  private apiClient = inject(ApiClientService);
  private cachedLicense: string | null = null;
  private readonly STORAGE_KEY = 'ag_grid_license';

  constructor() {
    this.loadCachedLicense();
  }

  initializeAgGridLicense(): Observable<string> {
    // Return cached license if available (already loaded by constructor)
    if (this.cachedLicense) {
      return of(this.cachedLicense);
    }

    // Fetch from API if not cached
    return this.apiClient.get<string | AgGridLicenseResponse>('/settings/ag-grid').pipe(
      map(response => {
        let license = '';
        if (typeof response === 'string') {
          license = response;
        } else if (response && typeof response === 'object') {
          license =
            (response as any).license ||
            (response as any).value ||
            (response as any).data ||
            (response as any).agGridLicense ||
            '';
        }

        return license;
      }),
      tap(license => {
        if (license) {
          this.cachedLicense = license;
          localStorage.setItem(this.STORAGE_KEY, license);
        }
      }),
      catchError(error => {
        return of('');
      })
    );
  }

  getAgGridLicense(): Observable<string> {
    // Return cached license if available (already loaded by constructor)
    if (this.cachedLicense) {
      return of(this.cachedLicense);
    }

    // Fetch from API if not cached
    return this.apiClient.get<string | AgGridLicenseResponse>('/settings/ag-grid').pipe(
      map(response => {
        let license = '';
        if (typeof response === 'string') {
          license = response;
        } else if (response && typeof response === 'object') {
          license =
            (response as any).license ||
            (response as any).value ||
            (response as any).data ||
            (response as any).agGridLicense ||
            '';
        }

        return license;
      }),
      tap(license => {
        if (license) {
          this.cachedLicense = license;
          localStorage.setItem(this.STORAGE_KEY, license);
        }
      }),
      catchError(error => {
        return of('');
      })
    );
  }

  private loadCachedLicense(): void {
    try {
      const storedLicense = localStorage.getItem(this.STORAGE_KEY);
      if (storedLicense) {
        this.cachedLicense = storedLicense;
      }
    } catch (error) {
      console.error('Failed to load cached AG Grid license from localStorage:', error);
    }
  }

  clearCachedLicense(): void {
    this.cachedLicense = null;
    localStorage.removeItem(this.STORAGE_KEY);
  }
}
